import BaseRangeDatepicker, {
  adjustToCurrentMonth,
} from 'components/atoms/base-range-datepicker';
import { endOfDate, startOfDate } from 'infrastructure/functions';
import { useTranslation } from 'react-i18next';
import { PresetKeyEnum } from 'infrastructure/enums';
import dayjs from 'dayjs';
import { type FC } from 'react';

import s from './styles.module.scss';

import type {
  Presets,
  RangePickerValue,
  TOnCalendarChange,
} from 'components/atoms/base-range-datepicker';
import type {
  Filters,
  AppendFilters,
  TInteractionFlag,
} from 'infrastructure/hooks/interactiveTimeDashboard/types';

interface Props {
  filters: Filters;
  appendFilters: AppendFilters;
  interactionFlagType: TInteractionFlag;
}

const InteractionDateRange: FC<Props> = ({
  appendFilters,
  filters,
  interactionFlagType,
}) => {
  const changeTimeRange: TOnCalendarChange = (
    dates,
    dateString,
    info,
    preset,
  ) => {
    const [start, end] = dates;

    if (!start && !end) {
      appendFilters({
        interactionFlagStartTime: undefined,
        interactionFlagEndTime: undefined,
        interactionFlagType: undefined,
      });
      return;
    }

    if (preset && start && end) {
      appendFilters({
        interactionFlagStartTime: startOfDate(start).getTime(),
        interactionFlagEndTime: endOfDate(end).getTime(),
        interactionFlagType,
      });
      return;
    }

    if (start && info.range === 'start') {
      appendFilters({
        interactionFlagStartTime: startOfDate(start).getTime(),
        interactionFlagType,
      });
    }
    if (end && info.range === 'end') {
      appendFilters({
        interactionFlagEndTime: endOfDate(end).getTime(),
        interactionFlagType,
      });
    }
  };

  const selectedRange: RangePickerValue = [
    filters.interactionFlagStartTime &&
    interactionFlagType === filters.interactionFlagType
      ? new Date(filters.interactionFlagStartTime)
      : null,
    filters.interactionFlagEndTime &&
    interactionFlagType === filters.interactionFlagType
      ? new Date(filters.interactionFlagEndTime)
      : null,
  ];
  const { t } = useTranslation();

  const excludedPresets = [
    PresetKeyEnum.PreviousMonth,
    PresetKeyEnum.TwoMonthsPast,
    PresetKeyEnum.ThreeMonthsPast,
  ];

  const dateRanges: Presets = [
    {
      label: (
        <span data-cy="ranges">
          {t('dates.lastCountDays', {
            count: 30,
          })}
        </span>
      ),
      value: adjustToCurrentMonth(dayjs().add(-30, 'd'), dayjs(), false),
      key: PresetKeyEnum.LastThirtyDays,
      index: 30,
    },
    {
      label: (
        <span data-cy="ranges">
          {t('dates.lastCountDays', {
            count: 45,
          })}
        </span>
      ),
      value: adjustToCurrentMonth(dayjs().add(-45, 'd'), dayjs(), false),
      key: PresetKeyEnum.LastFortyFiveDays,
      index: 45,
    },
  ];

  const currentDate = dayjs()
    .year(filters.year)
    .month(filters.month - 1);
  const currentMonthEnd = currentDate.endOf('month').add(1, 'hour');

  const label =
    interactionFlagType === 'interaction'
      ? t('labels.lastInteractionWithinDateRange')
      : t('labels.lastInteractionNotWithinDateRange');

  return (
    <div style={{ width: '100%' }}>
      <div className={s['interaction-date-range']}>
        <label
          htmlFor="interactionDateRange"
          data-cy="interactiveTimeDashboard-filter-interactionDateRange"
        >
          {label}
        </label>
        <div className={s['input-wrapper']}>
          <BaseRangeDatepicker
            withPresets
            presets={(defaults) => [
              ...defaults.filter(
                (el) => !excludedPresets.includes(el.key as any),
              ),
              ...dateRanges,
            ]}
            maxDate={currentMonthEnd.toDate()}
            onCalendarChange={changeTimeRange}
            value={selectedRange}
          />
        </div>
      </div>
    </div>
  );
};

export default InteractionDateRange;
